<template>
  <!-- Compact (list) mode -->
  <div v-if="compact" class="event-row" @click="handleCardClick">
    <span class="row-meta">{{ formattedDate }}</span>
    <span class="row-title">
      {{ event.title }}
      <span v-if="shouldShowGenre(event)" class="row-genre">{{ event.genre }}</span>
    </span>
    <span class="row-venue">{{ event.venue }}</span>
    <button
      v-if="canFavorite"
      class="row-fav"
      :class="{ 'row-fav--active': isFav }"
      @click.stop="onFavoriteClick"
      :aria-label="isFav ? 'Ta bort från favoriter' : 'Spara som favorit'"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" :fill="isFav ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    </button>
  </div>

  <!-- Normal card mode -->
  <div v-else ref="card" class="event-card" :class="{ 'is-focused': isFocused }" @click="handleCardClick">
    <div class="card-image-wrapper">
      <img
        v-if="event.venue === 'Valand'"
        loading="lazy"
        :src="`https://livegbg-test.herokuapp.com/api/proxy?url=${event.imageUrl}`"
        :alt="event.title"
        class="card-image"
      />
      <img
        v-else
        loading="lazy"
        :src="event.imageUrl"
        :alt="event.title"
        class="card-image"
      />
      <button
        v-if="canFavorite"
        class="card-fav"
        :class="{ 'card-fav--active': isFav }"
        @click.stop="onFavoriteClick"
        :aria-label="isFav ? 'Ta bort från favoriter' : 'Spara som favorit'"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" :fill="isFav ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </button>
    </div>
    <div class="card-body">
      <h3 class="event-title">{{ event.title }}</h3>
      <p class="event-meta">
        {{ formattedDate }} / {{ (event.venue || "").toUpperCase() }}
        <span v-if="shouldShowGenre(event)" class="event-genre">{{ event.genre }}</span>
      </p>
    </div>
  </div>
</template>

<script>
import { favoriteIds, toggleFavorite } from "@/utils/favorites";
import { isLoggedIn } from "@/utils/auth";
import { shouldShowGenre } from "@/utils/genreDisplay";

export default {
  name: "EventCard",
  emits: ["showEventInfo"],
  props: {
    event: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFocused: false,
    };
  },
  computed: {
    canFavorite() {
      // Både scrapade konserter och organizer-skapade external events
      // kan favoritmarkeras nu. Behöver bara ett ID att referera mot.
      return !!this.event._id;
    },
    isFav() {
      const id = this.event._id;
      return id ? favoriteIds.value.has(String(id)) : false;
    },
    formattedDate() {
      if (!this.event.date) return "";
      const months = [
        "JAN", "FEB", "MAR", "APR", "MAJ", "JUN",
        "JUL", "AUG", "SEP", "OKT", "NOV", "DEC",
      ];
      const datePart = this.event.date.split("T")[0];
      const timePart = this.event.date.includes("T")
        ? this.event.date.split("T")[1].substring(0, 5)
        : "";
      const [, month, day] = datePart.split("-");
      const monthName = months[parseInt(month) - 1];
      const dayNum = parseInt(day);
      return timePart && timePart !== "00:00"
        ? `${monthName} ${dayNum} / ${timePart}`
        : `${monthName} ${dayNum}`;
    },
  },
  mounted() {
    if (!this.compact && this.$refs.card) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.isFocused = entries[0].isIntersecting;
        },
        { threshold: 0.5 }
      );
      this.intersectionObserver.observe(this.$refs.card);
    }
  },
  beforeUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },
  methods: {
    // Importerad från utils — exposeras till template via methods.
    // Beslut: visa genre BARA om användaren är inloggad och AI-confidence
    // är >= 0.7 (eller admin manuellt satt). Auth-gate sker bara här i
    // frontend; backend exposerar fältet öppet.
    shouldShowGenre,
    async onFavoriteClick() {
      if (!isLoggedIn.value) {
        // Skicka utloggad user till login - låt routern hantera redirect tillbaka
        this.$router.push({
          path: "/login",
          query: { redirect: this.$route.fullPath },
        });
        return;
      }
      try {
        // Skicka hela event-objektet - toggleFavorite läser _isExternal
        // för att veta vilken eventType det är.
        await toggleFavorite(this.event);
      } catch (err) {
        if (this.$q) {
          this.$q.notify({
            type: "negative",
            message: "Kunde inte spara favoriten",
          });
        }
      }
    },
    handleCardClick() {
      this.$emit("showEventInfo", this.event);
    },
  },
};
</script>

<style scoped>
/* ── Normal card ── */
.event-card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  will-change: transform;
}

@media (min-width: 701px) {
  .event-card:hover {
    transform: rotate(2deg) translateY(-8px);
    box-shadow: 6px 14px 28px rgba(0, 0, 0, 0.13);
  }
}

@media (max-width: 700px) {
  .event-card:hover {
    transform: none;
  }
}

.event-card:hover .event-title {
  color: var(--color-accent);
}

.card-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background-color: var(--color-image-placeholder);
}

.card-fav {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 2;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, transform 0.15s ease, color 0.15s ease;
}

.card-fav:hover {
  background: rgba(0, 0, 0, 0.7);
  transform: scale(1.05);
}

.card-fav--active {
  color: #ff4d6d;
}

.event-genre {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 2px 10px;
  background: var(--color-accent, #cc1100);
  color: #fff;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.row-fav {
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease;
}

.row-fav:hover {
  color: var(--color-text);
}

.row-fav--active {
  color: #ff4d6d;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(100%);
  transition: filter 0.3s ease;
  display: block;
}

.event-card:hover .card-image {
  filter: grayscale(0%);
}

.card-body {
  padding: 0.75rem 0.25rem 0;
}

.event-title {
  font-family: "Playfair Display", serif;
  font-weight: 900;
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.2;
  color: var(--color-text);
  margin-bottom: 0.35rem;
  transition: color 0.2s ease;
}

.event-meta {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-bottom: 0.35rem;
}

.event-description {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  font-style: italic;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Compact row ── */
.event-row {
  display: grid;
  grid-template-columns: 7rem 1fr 8rem auto;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 0.25rem;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: background 0.15s ease;
}

.event-row:hover {
  background: rgba(204, 17, 0, 0.04);
}

.event-row:hover .row-title {
  color: var(--color-accent);
}

.row-meta {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  white-space: nowrap;
}

.row-title {
  font-family: "Playfair Display", serif;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-text);
  transition: color 0.15s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-venue {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Genre-badge i kompakta listläget — samma stil som .event-genre på
   kortet men mindre, ligger inline efter titeln (egen grid-cell skulle
   bryta radens kolumnlayout) */
.row-genre {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 1px 8px;
  background: var(--color-accent, #cc1100);
  color: #fff;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  vertical-align: middle;
}

@media (max-width: 500px) {
  .event-row {
    grid-template-columns: 5.5rem 1fr auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      "meta title fav"
      "meta venue fav";
    gap: 0 0.75rem;
  }

  .row-meta {
    grid-area: meta;
    align-self: start;
    padding-top: 0.15rem;
  }

  .row-title {
    grid-area: title;
    white-space: normal;
  }

  .row-venue {
    grid-area: venue;
    text-align: left;
    margin-top: 0.15rem;
    font-size: 0.65rem;
    color: var(--color-text-faint);
  }

  .row-fav {
    grid-area: fav;
    align-self: center;
  }
}

/* ── Mobile focus state ── */
@media (max-width: 700px) {
  /* Remove hover-triggered color changes on mobile */
  .event-card:hover .event-title {
    color: var(--color-text);
  }

  .event-card:hover .card-image {
    filter: grayscale(100%);
  }

  /* Card currently in view gets colored image and accent title */
  .event-card.is-focused .card-image {
    filter: grayscale(0%);
  }

  .event-card.is-focused .event-title {
    color: var(--color-accent);
  }
}
</style>


